// Progress as the logo's circle: a ring that fills as items get a result. The text next to it (or
// its accessible label) states the numbers; the ring itself is decorative.

export function ProgressRing({
  done,
  total,
  size = 48,
  label,
  showText = true,
  tone,
}: {
  readonly done: number;
  readonly total: number;
  readonly size?: number;
  /** Read by assistive technology; omit when the numbers are written next to the ring. */
  readonly label?: string | undefined;
  /** The count inside the ring (hidden on small rings). */
  readonly showText?: boolean;
  /** `defect`: complete with open findings. */
  readonly tone?: 'defect' | undefined;
}) {
  const stroke = size >= 56 ? 6 : size >= 40 ? 5 : 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const share = total === 0 ? 0 : Math.min(1, done / total);
  const state = tone ?? (share === 1 ? 'done' : 'open');
  return (
    <span
      className={`ring ring--${state}`}
      style={{ inlineSize: size, blockSize: size }}
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`}>
        <circle
          className="ring__track"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="ring__value"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - share)}
        />
      </svg>
      {showText && size >= 40 && (
        <span className="ring__text" aria-hidden="true">
          {done}/{total}
        </span>
      )}
    </span>
  );
}
